import { useEffect, useMemo, useRef, useState } from 'react';
import {
  useGetProjectQuery,
  usePatchProjectMutation,
  useUploadReportLogoMutation,
} from 'app/services/projectBoardApi';
import { useActualTabs } from '../../hooks/useProjectBoardData';

/**
 * REPORT LOGO TAB (`#report_logo` — `logo/view/logo.js` + logo.tmpl),
 * inventory §B8 / parity 199–203.
 *
 * GATE (parity 199): content renders ONLY when
 * `project.is_can_set_project_logo` — the mount stays EMPTY even though the
 * tab is reachable by hash (blank-panel parity; the menu entry itself is the
 * client-side append, Part A #15). Read-only is the `no-permissions` CSS
 * class (pointer-events lockout) keyed off the **'project'** tab access —
 * there is no 'report_logo' permission row; this is the legacy's WORKING
 * gating mechanism, kept as-is (no attribute-level disabling added).
 *
 * Fields (parity 201): `change .report_field` → PATCH /api/v1.0/projects/{id}
 * {field} riding the shared patchProject mutation — success re-syncs the
 * input from the echo (cache merge), failure reverts model + input
 * (optimistic undo).
 *
 * Logo upload (parity 202): client rejects files ≥ 3MB by flipping `.invalid`
 * on the `.max-size-note` spans (BOTH the label and the "3 MB" value carry
 * the class) — and the flag is NEVER cleared until a remount ([NEG]-quirk
 * faithful); an accepted file goes out as multipart PATCH `report_logo`
 * (uploadReportLogo mutation = the legacy raw XHR), success swaps the preview
 * (cache merge) and clears the picker.
 *
 * Reset (parity 203): PATCH {report_logo: null} — the legacy failure path's
 * bogus `{logo: prev}` model key is NOT ported ([NEG]-quirk; the visible
 * revert-the-preview behavior comes from the optimistic undo). Default:
 * PATCH all five report_* fields to null + resync inputs/preview.
 */

const REPORT_FIELDS: ReadonlyArray<{
  name: string;
  label: string;
  maxLength: number;
  className: string;
  rowClass: string;
}> = [
  {
    name: 'report_co',
    label: 'Company',
    maxLength: 45,
    className: 'report_field',
    rowClass: ' mt-15',
  },
  {
    name: 'report_city',
    label: 'City',
    maxLength: 30,
    className: 'report_field input-text',
    rowClass: '',
  },
  {
    name: 'report_state',
    label: 'State',
    maxLength: 30,
    className: 'report_field input-text',
    rowClass: '',
  },
  {
    name: 'report_country',
    label: 'Country',
    maxLength: 30,
    className: 'report_field input-text',
    rowClass: '',
  },
];

const ReportField = ({
  name,
  className,
  maxLength,
  value,
  onCommit,
}: {
  name: string;
  className: string;
  maxLength: number;
  value: string;
  onCommit: (name: string, value: string) => void;
}) => {
  const [draft, setDraft] = useState(value);
  const ref = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (document.activeElement !== ref.current) setDraft(value);
  }, [value]);

  const commit = () => {
    if (draft !== value) onCommit(name, draft);
  };

  return (
    <input
      ref={ref}
      type="text"
      name={name}
      className={className}
      value={draft}
      minLength={0}
      maxLength={maxLength}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit();
      }}
    />
  );
};

const ReportLogoTab = ({ projectId }: { projectId: number }) => {
  const { data: project } = useGetProjectQuery(projectId, {
    skip: !Number.isFinite(projectId),
  });
  const tabs = useActualTabs(projectId);
  const [patchProject] = usePatchProjectMutation();
  const [uploadLogo] = useUploadReportLogoMutation();

  /** ≥3MB picker rejection — sticky until remount ([NEG]-quirk faithful). */
  const [maxSizeInvalid, setMaxSizeInvalid] = useState(false);

  // The view's `permissions` = the 'project' tab access (no report_logo row).
  const access = useMemo(
    () => !!tabs?.find((t) => t.value === 'project')?.access,
    [tabs]
  );

  // Legacy assembly resolves permissions + project before constructing the
  // view; render nothing until both are here.
  if (!project || !tabs) return null;
  // Blank-panel parity: without the flag the mount stays empty.
  if (!project.is_can_set_project_logo) return null;

  const onFieldCommit = (name: string, value: string) => {
    // chnageReportField (sic): PATCH {field}; echo re-sync + failure revert
    // are the patchProject optimistic/echo-merge machinery.
    patchProject({ id: projectId, body: { [name]: value } });
  };

  const onLogoPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const file = input.files?.[0];
    if (!file) return;
    const fileSizeMb = file.size / 1048576;
    if (fileSizeMb < 3) {
      uploadLogo({ id: projectId, file })
        .unwrap()
        .then(() => {
          input.value = ''; // success clears the picker (parity 202)
        })
        .catch(() => undefined); // legacy: non-200 leaves everything as-is
    } else {
      // Oversize: flag the max-size notes; the picker keeps the file (legacy).
      setMaxSizeInvalid(true);
    }
  };

  const onResetLogo = () => {
    patchProject({ id: projectId, body: { report_logo: null } });
  };

  const onResetForm = () => {
    patchProject({
      id: projectId,
      body: {
        report_co: null,
        report_city: null,
        report_state: null,
        report_country: null,
        report_logo: null,
      },
    });
  };

  const maxSizeClass = `label-note max-size-note${maxSizeInvalid ? ' invalid' : ''}`;
  const maxSizeValueClass = `label-note with_p_l max-size-note${
    maxSizeInvalid ? ' invalid' : ''
  }`;

  return (
    <div className="content">
      <div
        className={`report-company-wrapper content-item ${access ? '' : 'no-permissions'}`}
      >
        <div className="report-company-row">
          <div className="row">
            <div className="col-xs-4 height-29">
              <h2 className="title-block">REPORT LOGO &amp; COMPANY</h2>
            </div>
            <div className="col-xs-8 height-29">
              <div className="reset-wrapper">
                <button className="btn reset-btn reset-form" onClick={onResetForm}>
                  Default
                </button>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-xs-4 height-29 justify-content-between logo-row">
              <div className="name">Logo</div>
              <div className="reset_or_upload">
                <button className="btn reset-btn reset-logo" onClick={onResetLogo}>
                  Reset
                </button>
                <label className="upload-logo">
                  <span className="upload-text">Upload</span>
                  <form id="report_logo_form">
                    <input
                      id="report_logo_input"
                      type="file"
                      name="report_logo"
                      accept="image/png"
                      onChange={onLogoPick}
                    />
                  </form>
                </label>
              </div>
            </div>
            <div className="col-xs-8 logo-row fix-height">
              <img
                id="report_logo_img"
                alt="report_logo"
                src={(project.report_logo as string | null) ?? ''}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-xs-4">
              <span className="label-note pt-10">File type</span>
              <span className={maxSizeClass}>Max file size</span>
              <span className="label-note">Aspect ratio</span>
            </div>
            <div className="col-xs-8">
              <span className="label-note with_p_l pt-10">PNG</span>
              <span className={maxSizeValueClass}>3 MB</span>
              <span className="label-note with_p_l">5 x 2</span>
            </div>
          </div>
          {REPORT_FIELDS.map((field) => (
            <div className="row" key={field.name}>
              <div className={`col-xs-4 height-29${field.rowClass}`}>
                <span className="name">{field.label}</span>
              </div>
              <div className={`col-xs-8${field.rowClass}`}>
                <ReportField
                  name={field.name}
                  className={field.className}
                  maxLength={field.maxLength}
                  value={(project[field.name] as string | null) ?? ''}
                  onCommit={onFieldCommit}
                />
              </div>
            </div>
          ))}
          <div className="note">
            Note: The manufacturer&apos;s name, contact and logo will be visible
            exclusively in the reports.
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportLogoTab;
